import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Empty, Modal, Chip, EmbeddedActionBar } from "../components/ui-bits";
import { Plus, Search, Archive, ArchiveRestore, Edit3, X } from "lucide-react";
import { useBrandPermissions } from "../lib/brand";
import { useT, useI18n } from "../lib/i18n";
import { COUNTRIES, countryLabel } from "../lib/countries";
import { useAutoOpenFromQuery } from "../lib/useAutoOpenFromQuery";

// ---------- constants (mirror backend enums) ----------
const CLIENT_TYPES = [
  { key: "shop",     label: "Negozio / rivenditore" },
  { key: "private",  label: "Cliente privato" },
  { key: "showroom", label: "Showroom / agente" },
  { key: "buyer",    label: "Buyer / department store" },
];
const CLIENT_TYPE_KEYS = CLIENT_TYPES.map(t => t.key);
const typeLabel = (k) => CLIENT_TYPES.find(t => t.key === k)?.label || k || "—";
const SOURCES = ["Manuale", "Shopify", "Wix", "WooCommerce", "Altro"];
const STATUSES = ["Attivo", "Archiviato"];
const LANGUAGES = [
  { code: "it", it: "Italiano", en: "Italian" },
  { code: "en", it: "Inglese", en: "English" },
  { code: "fr", it: "Francese", en: "French" },
  { code: "es", it: "Spagnolo", en: "Spanish" },
  { code: "de", it: "Tedesco", en: "German" },
];
const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "JPY"];

const emptyAddress = () => ({ line1: "", line2: "", zip: "", city: "", province: "", country: "" });
const emptyForm = () => ({
  client_type: "shop",
  name: "",
  first_name: "",
  last_name: "",
  contact_name: "",
  buyer_name: "",
  store: "",
  zones: "",
  email: "",
  phone: "",
  website: "",
  vat_or_tax_id: "",
  country: "",
  city: "",
  billing_address: emptyAddress(),
  shipping_address: emptyAddress(),
  same_shipping: true,
  preferred_language: "it",
  currency: "EUR",
  tags: [],
  notes: "",
  status: "Attivo",
});

// ---------- Source chip — colour maps to brand tokens for light/dark support ----------
function sourceChipStyle(src) {
  // keep contrast on light+dark by using rgba() with alpha; the label is always readable
  const map = {
    Manuale:    { bg: "rgba(115,115,115,0.14)", fg: "var(--text-primary)" },
    Shopify:    { bg: "rgba(15,133,84,0.16)",   fg: "#0F8554" },
    Wix:        { bg: "rgba(93,60,180,0.16)",   fg: "#5D3CB4" },
    WooCommerce:{ bg: "rgba(122,49,167,0.16)",  fg: "#7A31A7" },
    Altro:      { bg: "rgba(212,175,55,0.18)",  fg: "#8A6C0A" },
  };
  return map[src] || map.Altro;
}

function SourceChip({ value }) {
  const s = sourceChipStyle(value || "Manuale");
  return (
    <span
      className="chip"
      style={{ background: s.bg, color: s.fg, borderColor: s.bg }}
      title={value}
      data-testid={`source-chip-${value}`}
    >
      <span className="w-1.5 h-1.5 rounded-full inline-block mr-1.5" style={{ background: s.fg }}/>
      {value || "—"}
    </span>
  );
}

function StatusChip({ value }) {
  const tone = value === "Archiviato" ? "default" : "green";
  return <Chip tone={tone}>{value || "Attivo"}</Chip>;
}

// ---------- Country combobox (searchable, ISO alpha-2) ----------
function CountrySelect({ value, onChange, testId, placeholder }) {
  const { lang } = useI18n();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const label = value ? countryLabel(value, lang) : "";
  const filtered = useMemo(() => {
    if (!q) return COUNTRIES;
    const s = q.toLowerCase();
    return COUNTRIES.filter(c =>
      c.code.toLowerCase().includes(s) || c.it.toLowerCase().includes(s) || c.en.toLowerCase().includes(s)
    );
  }, [q]);

  return (
    <div className="relative">
      <input
        data-testid={testId}
        readOnly
        onClick={()=>setOpen(true)}
        placeholder={placeholder || t("client.hint.country_search")}
        value={label}
        className="input-dark cursor-pointer pr-8"
      />
      {value && (
        <button type="button" onClick={()=>{onChange(""); setQ("");}}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted,#737373)] hover:text-[color:var(--text-primary)]">
          <X size={14}/>
        </button>
      )}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={()=>setOpen(false)}/>
          <div className="absolute z-50 mt-1 w-full max-h-72 overflow-auto shadow-lg"
               style={{ background: "var(--surface, #fff)", border: "1px solid var(--border,#E5E5E5)" }}>
            <div className="p-2 sticky top-0" style={{ background: "var(--surface, #fff)", borderBottom: "1px solid var(--border,#E5E5E5)" }}>
              <input
                autoFocus
                data-testid={`${testId}-search`}
                className="input-dark text-xs"
                placeholder={t("client.hint.country_search")}
                value={q}
                onChange={(e)=>setQ(e.target.value)}
              />
            </div>
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-[color:var(--text-muted,#737373)]">—</div>
            ) : filtered.map(c => (
              <button
                key={c.code}
                type="button"
                onClick={()=>{ onChange(c.code); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-black/5 flex items-center justify-between ${value===c.code?"font-medium":""}`}
              >
                <span>{lang==="en" ? c.en : c.it}</span>
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted,#737373)]">{c.code}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Address block ----------
function AddressBlock({ value, onChange, prefix, disabled }) {
  const t = useT();
  const set = (k, v) => onChange({ ...(value || emptyAddress()), [k]: v });
  const a = value || emptyAddress();
  return (
    <div className="space-y-2 opacity-100">
      <input data-testid={`${prefix}-line1`} className="input-dark" placeholder={t("client.addr.line1")}
             value={a.line1||""} onChange={(e)=>set("line1", e.target.value)} disabled={disabled}/>
      <input data-testid={`${prefix}-line2`} className="input-dark" placeholder={t("client.addr.line2")}
             value={a.line2||""} onChange={(e)=>set("line2", e.target.value)} disabled={disabled}/>
      <div className="grid grid-cols-3 gap-2">
        <input data-testid={`${prefix}-zip`} className="input-dark" placeholder={t("client.addr.zip")}
               value={a.zip||""} onChange={(e)=>set("zip", e.target.value)} disabled={disabled}/>
        <input data-testid={`${prefix}-city`} className="input-dark col-span-2" placeholder={t("client.addr.city")}
               value={a.city||""} onChange={(e)=>set("city", e.target.value)} disabled={disabled}/>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid={`${prefix}-province`} className="input-dark" placeholder={t("client.addr.province")}
               value={a.province||""} onChange={(e)=>set("province", e.target.value)} disabled={disabled}/>
        <CountrySelect testId={`${prefix}-country`} value={a.country||""} onChange={(v)=>set("country", v)}/>
      </div>
    </div>
  );
}

// ---------- Tags input ----------
function TagsInput({ value, onChange, testId }) {
  const [text, setText] = useState("");
  const add = () => { const v = text.trim(); if (!v) return; if (!(value||[]).includes(v)) onChange([...(value||[]), v]); setText(""); };
  return (
    <div className="input-dark flex flex-wrap gap-1 items-center min-h-[38px]" data-testid={testId}>
      {(value||[]).map((tag, i) => (
        <span key={i} className="chip inline-flex items-center gap-1">
          {tag}
          <button type="button" onClick={()=>onChange(value.filter((_,j)=>j!==i))} className="text-[color:var(--text-muted,#737373)] hover:text-[color:var(--text-primary)]"><X size={10}/></button>
        </span>
      ))}
      <input
        className="flex-1 min-w-[80px] bg-transparent outline-none text-sm"
        value={text}
        onChange={(e)=>setText(e.target.value)}
        onKeyDown={(e)=>{ if (e.key==="Enter" || e.key===",") { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder="+ tag"
      />
    </div>
  );
}

// ==========================================================
export default function ClientsPage({ embedded = false } = {}) {
  const { canEdit, canAdmin } = useBrandPermissions();
  const t = useT();
  const { lang } = useI18n();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ client_type: "", source: "", status: "", include_archived: false });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    const params = { search: search || undefined };
    if (filters.client_type) params.client_type = filters.client_type;
    if (filters.source)      params.source = filters.source;
    if (filters.status)      params.status = filters.status;
    if (filters.include_archived) params.include_archived = true;
    return api.get("/clients", { params }).then(r => setList(r.data));
  };
  useEffect(() => { load(); }, [search, filters]);

  const open = (row) => {
    if (row) {
      setForm({ ...emptyForm(), ...row,
        billing_address: row.billing_address || emptyAddress(),
        shipping_address: row.shipping_address || emptyAddress(),
        tags: row.tags || [],
      });
      setEditId(row.id);
    } else {
      setForm(emptyForm()); setEditId(null);
    }
    setError(""); setFieldErrors({}); setModal(true);
  };
  useAutoOpenFromQuery(list, open);

  const validate = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = t("client.err.required");
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = t("client.err.email_invalid");
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const save = async (e) => {
    e.preventDefault(); setError("");
    if (!validate()) return;
    setSaving(true);
    // when same_shipping is on, don't send a separate shipping_address
    const payload = { ...form };
    if (payload.same_shipping) delete payload.shipping_address;
    // omit id/technical fields (updated server-side)
    delete payload.id; delete payload.brand_id; delete payload.organization_id;
    delete payload.created_at; delete payload.updated_at; delete payload.source;
    delete payload.external_customer_id;
    try {
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post("/clients", payload);
      setModal(false); load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const archive = async (row) => {
    if (!window.confirm(t("client.confirm.archive"))) return;
    await api.post(`/clients/${row.id}/archive`); load();
  };
  const unarchive = async (row) => {
    await api.post(`/clients/${row.id}/unarchive`); load();
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div data-testid="clients-page">
      {!embedded && (
      <PageHeader
        eyebrow="Brands & CRM"
        title={t("clients.title")}
        description={t("clients.desc")}
        action={canEdit && (
          <button data-testid="new-client-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2">
            <Plus size={14}/> {t("clients.new")}
          </button>
        )}
      />
      )}

      {embedded && canEdit && (
        <EmbeddedActionBar action={
          <button data-testid="new-client-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2">
            <Plus size={14}/> {t("clients.new")}
          </button>
        }/>
      )}

      {/* Search + Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted,#737373)]"/>
          <input data-testid="clients-search" placeholder={t("common.search")} value={search} onChange={(e)=>setSearch(e.target.value)} className="input-dark pl-9"/>
        </div>
        <select data-testid="clients-filter-type" className="input-dark max-w-xs" value={filters.client_type} onChange={(e)=>setFilters(f=>({...f, client_type: e.target.value}))}>
          <option value="">{t("client.filter.all_types", "Tutti i tipi")}</option>
          {CLIENT_TYPES.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
        </select>
        <select data-testid="clients-filter-source" className="input-dark max-w-xs" value={filters.source} onChange={(e)=>setFilters(f=>({...f, source: e.target.value}))}>
          <option value="">{t("client.filter.all_sources")}</option>
          {SOURCES.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="clients-filter-status" className="input-dark max-w-xs" value={filters.status} onChange={(e)=>setFilters(f=>({...f, status: e.target.value}))}>
          <option value="">{t("client.filter.all_statuses")}</option>
          {STATUSES.map(v => <option key={v} value={v}>{t(`client.status.${v.toLowerCase()}`, v)}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-xs">
          <input type="checkbox" data-testid="clients-filter-include-archived" checked={filters.include_archived} onChange={(e)=>setFilters(f=>({...f, include_archived: e.target.checked}))}/>
          {t("client.filter.include_archived")}
        </label>
      </div>

      {list.length === 0 ? (
        <Empty
          title={t("clients.empty")}
          hint={canEdit ? t("clients.empty_hint") : t("clients.empty_hint_ro")}
          cta={canEdit ? { label: t("clients.cta_first"), onClick: ()=>open(null) } : null}
          ctaTestId="clients-empty-cta"
        />
      ) : (
        <div className="card-dark overflow-x-auto">
          <table className="tbl" data-testid="clients-table">
            <thead><tr>
              <th>{t("client.tbl.name")}</th>
              <th>{t("client.tbl.contact")}</th>
              <th>{t("client.tbl.email")}</th>
              <th>{t("client.tbl.phone")}</th>
              <th>{t("client.tbl.location")}</th>
              <th>{t("client.tbl.type")}</th>
              <th>{t("client.tbl.source")}</th>
              <th>{t("client.tbl.status")}</th>
              <th></th>
            </tr></thead>
            <tbody>
              {list.map(c => (
                <tr key={c.id} data-testid={`client-row-${c.id}`}>
                  <td className="text-[color:var(--text-primary)] font-medium">{c.name}</td>
                  <td className="text-[color:var(--text-secondary,#525252)]">{c.contact_name || "—"}</td>
                  <td className="text-[color:var(--text-secondary,#525252)]">{c.email || "—"}</td>
                  <td>{c.phone || "—"}</td>
                  <td>{[c.city, countryLabel(c.country, lang)].filter(Boolean).join(", ") || "—"}</td>
                  <td>{typeLabel(c.client_type)}</td>
                  <td><SourceChip value={c.source || "Manuale"}/></td>
                  <td><StatusChip value={c.status || "Attivo"}/></td>
                  <td className="text-right whitespace-nowrap">
                    {canEdit && <>
                      <button onClick={()=>open(c)} title={t("common.edit")} className="text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary)] mr-3" data-testid={`client-edit-${c.id}`}><Edit3 size={14}/></button>
                      {c.status === "Archiviato" ? (
                        <button onClick={()=>unarchive(c)} title={t("client.action.unarchive")} className="text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary)]" data-testid={`client-unarchive-${c.id}`}><ArchiveRestore size={14}/></button>
                      ) : (
                        <button onClick={()=>archive(c)} title={t("client.action.archive")} className="text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary)]" data-testid={`client-archive-${c.id}`}><Archive size={14}/></button>
                      )}
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modal} onClose={()=>setModal(false)} title={editId ? t("client.modal.edit") : t("client.modal.new")} wide>
        <form onSubmit={save} className="space-y-6">
          {/* Section: identity */}
          <section>
            <div className="label-caps mb-3">{t("client.section.identity", "Identità")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldWrap label={t("client.field.client_type", "Tipo cliente")} required>
                <select data-testid="client-form-client_type" className="input-dark" value={form.client_type}
                  onChange={(e)=>{
                    const v = e.target.value;
                    setField("client_type", v);
                    // keep `name` coherent when switching away from private
                    if (v !== "private" && !form.name) setField("name", form.first_name && form.last_name ? `${form.first_name} ${form.last_name}` : "");
                  }}>
                  {CLIENT_TYPES.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
                </select>
              </FieldWrap>

              {form.client_type === "private" ? (
                <>
                  <FieldWrap label="Nome" required>
                    <input data-testid="client-form-first_name" className="input-dark" required
                      value={form.first_name||""}
                      onChange={(e)=>{
                        const first = e.target.value;
                        setField("first_name", first);
                        setField("name", `${first} ${form.last_name||""}`.trim());
                      }}/>
                  </FieldWrap>
                  <FieldWrap label="Cognome" required>
                    <input data-testid="client-form-last_name" className="input-dark" required
                      value={form.last_name||""}
                      onChange={(e)=>{
                        const last = e.target.value;
                        setField("last_name", last);
                        setField("name", `${form.first_name||""} ${last}`.trim());
                      }}/>
                  </FieldWrap>
                </>
              ) : (
                <FieldWrap label={form.client_type === "showroom" ? "Nome showroom" : form.client_type === "buyer" ? "Store / department store" : "Ragione sociale"} required error={fieldErrors.name}>
                  <input data-testid="client-form-name" className="input-dark" required value={form.name} onChange={(e)=>setField("name", e.target.value)}/>
                </FieldWrap>
              )}

              {(form.client_type === "shop" || form.client_type === "showroom") && (
                <FieldWrap label="Referente">
                  <input data-testid="client-form-contact_name" className="input-dark" value={form.contact_name||""} onChange={(e)=>setField("contact_name", e.target.value)}/>
                </FieldWrap>
              )}
              {form.client_type === "buyer" && (
                <FieldWrap label="Buyer">
                  <input data-testid="client-form-buyer_name" className="input-dark" value={form.buyer_name||""} onChange={(e)=>setField("buyer_name", e.target.value)}/>
                </FieldWrap>
              )}
              {form.client_type === "showroom" && (
                <FieldWrap label="Zone di competenza">
                  <input data-testid="client-form-zones" className="input-dark" placeholder="Italia Nord, Francia, UK..." value={form.zones||""} onChange={(e)=>setField("zones", e.target.value)}/>
                </FieldWrap>
              )}
              {(form.client_type === "shop" || form.client_type === "buyer" || form.client_type === "showroom") && (
                <FieldWrap label="P.IVA / Codice fiscale">
                  <input data-testid="client-form-vat" className="input-dark" value={form.vat_or_tax_id||""} onChange={(e)=>setField("vat_or_tax_id", e.target.value)}/>
                </FieldWrap>
              )}
              <FieldWrap label={t("client.field.status", "Stato")}>
                <select data-testid="client-form-status" className="input-dark" value={form.status} onChange={(e)=>setField("status", e.target.value)}>
                  {STATUSES.map(v => <option key={v} value={v}>{t(`client.status.${v.toLowerCase()}`, v)}</option>)}
                </select>
              </FieldWrap>
            </div>
          </section>

          {/* Section: contacts */}
          <section>
            <div className="label-caps mb-3">{t("client.section.contacts")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldWrap label={t("client.field.email", "Email")} error={fieldErrors.email}>
                <input data-testid="client-form-email" type="email" className="input-dark" value={form.email||""} onChange={(e)=>setField("email", e.target.value)}/>
              </FieldWrap>
              <FieldWrap label={t("client.field.phone")}>
                <input data-testid="client-form-phone" className="input-dark" value={form.phone||""} onChange={(e)=>setField("phone", e.target.value)}/>
              </FieldWrap>
              <FieldWrap label={t("client.field.website")}>
                <input data-testid="client-form-website" className="input-dark" placeholder="https://..." value={form.website||""} onChange={(e)=>setField("website", e.target.value)}/>
              </FieldWrap>
              <FieldWrap label={t("client.field.city")}>
                <input data-testid="client-form-city" className="input-dark" value={form.city||""} onChange={(e)=>setField("city", e.target.value)}/>
              </FieldWrap>
              <FieldWrap label={t("client.field.country")}>
                <CountrySelect testId="client-form-country" value={form.country||""} onChange={(v)=>setField("country", v)}/>
              </FieldWrap>
            </div>
          </section>

          {/* Section: addresses */}
          <section>
            <div className="label-caps mb-3">{t("client.section.addresses")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs mb-2 text-[color:var(--text-secondary,#525252)]">{t("client.field.billing_address")}</div>
                <AddressBlock value={form.billing_address} onChange={(v)=>setField("billing_address", v)} prefix="client-form-billing"/>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-[color:var(--text-secondary,#525252)]">{t("client.field.shipping_address")}</div>
                  <label className="inline-flex items-center gap-2 text-xs">
                    <input data-testid="client-form-same_shipping" type="checkbox" checked={!!form.same_shipping} onChange={(e)=>setField("same_shipping", e.target.checked)}/>
                    {t("client.field.same_shipping")}
                  </label>
                </div>
                <AddressBlock value={form.same_shipping ? form.billing_address : form.shipping_address}
                  onChange={(v)=>setField("shipping_address", v)}
                  prefix="client-form-shipping"
                  disabled={!!form.same_shipping}/>
              </div>
            </div>
          </section>

          {/* Section: preferences */}
          <section>
            <div className="label-caps mb-3">{t("client.section.preferences")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldWrap label={t("client.field.preferred_language")}>
                <select data-testid="client-form-preferred_language" className="input-dark" value={form.preferred_language||"it"} onChange={(e)=>setField("preferred_language", e.target.value)}>
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{lang==="en"?l.en:l.it}</option>)}
                </select>
              </FieldWrap>
              <FieldWrap label={t("client.field.currency")}>
                <select data-testid="client-form-currency" className="input-dark" value={form.currency||"EUR"} onChange={(e)=>setField("currency", e.target.value)}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </FieldWrap>
              <FieldWrap label={t("client.field.tags")}>
                <TagsInput testId="client-form-tags" value={form.tags||[]} onChange={(v)=>setField("tags", v)}/>
              </FieldWrap>
              <FieldWrap label={t("client.field.source")}>
                <div className="input-dark flex items-center gap-2 opacity-70 pointer-events-none">
                  <SourceChip value={editId ? (form.source || "Manuale") : "Manuale"}/>
                </div>
              </FieldWrap>
              <div className="md:col-span-2">
                <FieldWrap label={t("client.field.notes")}>
                  <textarea data-testid="client-form-notes" rows={3} className="input-dark" value={form.notes||""} onChange={(e)=>setField("notes", e.target.value)}/>
                </FieldWrap>
              </div>
            </div>
          </section>

          {error && <div data-testid="client-form-error" className="text-red-600 text-sm">{error}</div>}

          <div className="flex justify-end gap-3 pt-2 border-t border-[color:var(--border,#E5E5E5)]">
            <button type="button" onClick={()=>setModal(false)} className="btn-ghost">{t("client.modal.cancel")}</button>
            <button type="submit" disabled={saving} data-testid="client-form-save" className="btn-gold">{t("client.modal.save")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function FieldWrap({ label, required, error, children }) {
  return (
    <div>
      <label className="label-caps block mb-2">
        {label}{required && <span className="text-[color:var(--accent,#D4AF37)] ml-1">*</span>}
      </label>
      {children}
      {error && <div className="text-red-600 text-xs mt-1">{error}</div>}
    </div>
  );
}
